import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import type { VideoPresenterOption } from "shared";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { Button } from "../components/Button";
import {
	useVideoActions,
	useVideoAvailability,
	useVideoGenerations,
	useVideoOptions,
	useVideoUsage,
} from "../video/useVideoGenerations";

const STATUS_LABEL: Record<string, string> = {
	queued: "Waiting",
	thinking: "In progress",
	reviewing: "In progress",
	generating: "In progress",
	completed: "Ready",
	failed: "Didn't finish",
	cancelled: "Cancelled",
};

function DefaultSelect(props: {
	label: string;
	value: string;
	options: VideoPresenterOption[];
	onChange: (value: string) => void;
}) {
	return (
		<Form.Group className="mb-2">
			<Form.Label className="small mb-1">{props.label}</Form.Label>
			<Form.Select
				size="sm"
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			>
				<option value="">Let Moxie pick</option>
				{props.options.map((o) => (
					<option key={o.id} value={o.id}>
						{o.name}
					</option>
				))}
			</Form.Select>
		</Form.Group>
	);
}

/** Story 5.3 defaults plus this month's minutes; Moxie-branded per Q7. */
export const VideoCreationCard = () => {
	const availability = useVideoAvailability();
	const enabled = availability.enabled;
	const options = useVideoOptions(enabled);
	const usage = useVideoUsage(enabled);
	const history = useVideoGenerations(null, enabled, 5);
	const { saveDefaults } = useVideoActions();
	const emit = useAlertEmit();
	const [avatarId, setAvatarId] = useState("");
	const [voiceId, setVoiceId] = useState("");
	const [styleId, setStyleId] = useState("");

	useEffect(() => {
		const defaults = options.data?.defaults;
		if (defaults) {
			setAvatarId(defaults.avatarId ?? "");
			setVoiceId(defaults.voiceId ?? "");
			setStyleId(defaults.styleId ?? "");
		}
	}, [options.data]);

	if (!availability.flagOn) {
		return null;
	}

	return (
		<div className="app-connect">
			<div className="app-connect-header">
				<div className="app-connect-metadata">
					<span className="app-connect-heygen-logo" aria-hidden>
						▶
					</span>
					<div>
						<div>Video creation</div>
						<div>Powered by HeyGen</div>
					</div>
				</div>
			</div>
			<div className="app-connect-spacer" />

			{!enabled ? (
				<p className="text-muted small mb-0">
					{availability.reason ?? "Video creation isn't available."}
				</p>
			) : (
				<>
					{usage.data && (
						<div className="app-connect-page-selection">
							This month
							<span className="active">
								{usage.data.minutesUsed} min · {usage.data.videosThisMonth}{" "}
								video{usage.data.videosThisMonth === 1 ? "" : "s"}
							</span>
						</div>
					)}
					{usage.data && (
						<div className="app-connect-page-selection">
							Today
							<span className="active">
								{usage.data.minutesRequestedToday} of{" "}
								{usage.data.dailyCapMinutes} min
							</span>
						</div>
					)}

					{options.data && (
						<form
							className="mt-2"
							onSubmit={(e) => {
								e.preventDefault();
								saveDefaults.mutate(
									{
										avatarId: avatarId || null,
										voiceId: voiceId || null,
										styleId: styleId || null,
									},
									{
										onSuccess: () =>
											emit({
												type: AlertType.SUCCESS,
												content: "Video defaults saved",
											}),
										onError: (error) =>
											emit({
												type: AlertType.ERROR,
												content:
													parseError(error) ?? "Couldn't save your defaults.",
											}),
									},
								);
							}}
						>
							<DefaultSelect
								label="Presenter"
								value={avatarId}
								options={options.data.avatars}
								onChange={setAvatarId}
							/>
							<DefaultSelect
								label="Voice"
								value={voiceId}
								options={options.data.voices}
								onChange={setVoiceId}
							/>
							<DefaultSelect
								label="Style"
								value={styleId}
								options={options.data.styles}
								onChange={setStyleId}
							/>
							<Button
								type="submit"
								variant="secondary"
								full
								loading={saveDefaults.isPending}
							>
								Save defaults
							</Button>
						</form>
					)}
					{options.isError && (
						<p className="text-muted small">
							Presenter choices couldn't load right now.
						</p>
					)}

					{(history.data?.length ?? 0) > 0 && (
						<ul className="list-unstyled small mt-3 mb-0">
							{history.data?.map((video) => (
								<li
									key={video.id}
									className="d-flex justify-content-between gap-2"
								>
									<span className="text-truncate">{video.title}</span>
									<span className="text-muted text-nowrap">
										{STATUS_LABEL[video.status] ?? video.status}
										{video.charged ? ` · ${video.actualSeconds}s` : ""}
									</span>
								</li>
							))}
						</ul>
					)}
				</>
			)}
		</div>
	);
};
